import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { SettingsProvider } from './contexts/SettingsContext'
import { ModelRepositoryProvider } from './contexts/ModelRepositoryContext'
import { initSentry } from './services/sentry'
import { warmNodeLayoutMetrics } from './nodes/nodeLayoutHelpers'
import { installPointerFocusGuard } from './utils/pointerFocusGuard'
import { installSessionContentKeyGate } from './utils/sessionContentKeyGate'
import './styles/theme.css'
import './styles/component-tokens.css'
import './styles/index.css'

// Initialize Sentry before React renders (prod-only, no-op in dev)
initSentry();
installPointerFocusGuard();
installSessionContentKeyGate();
warmNodeLayoutMetrics();

// Development-only log bridge
if (import.meta.env.DEV) {
  if (import.meta.env.VITE_LOG_BRIDGE_ENABLED === 'true') {
    import('./dev/logBridge.js').then(module => {
      module.init();
    }).catch(error => {
      console.error('Failed to initialize log bridge:', error);
    });
  }

  import('./dev/designLabPreviewBridge.js').then((module) => {
    module.initDesignLabPreviewBridge();
  }).catch((error) => {
    console.error('Failed to initialize design lab preview bridge:', error);
  });
}

// Dev-only mini composer pull-bar harness — access via #dev-mini-composer-pullbar
if (import.meta.env.DEV && window.location.hash.startsWith('#dev-mini-composer-pullbar')) {
  import('./dev/MiniComposerPullBarTest.jsx').then(({ default: MiniComposerPullBarTest }) => {
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ThemeProvider>
          <MiniComposerPullBarTest />
        </ThemeProvider>
      </React.StrictMode>,
    );
  });
} else if (
  import.meta.env.DEV
  && (
    window.location.hash.startsWith('#dev-components')
    || window.location.hash.startsWith('#dev-camera-explorer')
    || window.location.hash.startsWith('#dev-tuning-lab')
    || window.location.hash.startsWith('#dev-design-lab')
    || window.location.hash.startsWith('#dev-glow-audit')
    || window.location.hash.startsWith('#dev-module-actions')
    || window.location.hash.startsWith('#dev-mini-composer-modes')
    || window.location.hash.startsWith('#dev-picker-drop-zone')
    || window.location.hash.startsWith('#dev-agent-mark')
    || window.location.hash.startsWith('#dev-selection-ring')
    || window.location.hash.startsWith('#dev-enhance-sparkle')
    || window.location.hash.startsWith('#dev-slider-sandbox')
    || window.location.hash.startsWith('#dev-toasts')
    || window.location.hash.startsWith('#dev-design-system')
  )
) {
  import('./dev/DesignSystemDashboard.jsx').then(({ default: DesignSystemDashboard }) => {
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ThemeProvider>
          <DesignSystemDashboard />
        </ThemeProvider>
      </React.StrictMode>,
    );
  });
} else if (import.meta.env.DEV && window.location.hash.startsWith('#dev-contour-sweep')) {
  import('./dev/ContourSweepPlayground.jsx').then(({ default: ContourSweepPlayground }) => {
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ThemeProvider>
          <ContourSweepPlayground />
        </ThemeProvider>
      </React.StrictMode>,
    );
  });
} else if (import.meta.env.DEV && window.location.hash === '#video-layer-probe') {
  import('./dev/VideoLayerProbe.jsx').then(({ default: VideoLayerProbe }) => {
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <VideoLayerProbe />
      </React.StrictMode>,
    );
  });
} else if (import.meta.env.DEV && window.location.hash === '#effect-preview-lab') {
  import('./dev/EffectPreviewLab.jsx').then(({ default: EffectPreviewLab }) => {
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <EffectPreviewLab />
      </React.StrictMode>,
    );
  });
} else if (import.meta.env.DEV && window.location.hash === '#node-fidelity-lab') {
  import('./dev/NodeFidelityLab.jsx').then(({ default: NodeFidelityLab }) => {
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ModelRepositoryProvider autoLoad={false}>
          <ThemeProvider>
            <SettingsProvider>
              <NodeFidelityLab />
            </SettingsProvider>
          </ThemeProvider>
        </ModelRepositoryProvider>
      </React.StrictMode>,
    );
  });
} else {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <AuthProvider>
        <ModelRepositoryProvider>
          <ThemeProvider>
            <SettingsProvider>
              <App />
            </SettingsProvider>
          </ThemeProvider>
        </ModelRepositoryProvider>
      </AuthProvider>
    </React.StrictMode>,
  );
}
